// Seasonal Themes Configuration
// Easy to add new seasonal events and themes

export const SEASONAL_THEMES = {
  halloween: {
    id: 'halloween',
    name: 'Halloween Spooktacular',
    enabled: true,
    // Active from October 20 to November 2
    startDate: { month: 10, day: 20 }, // October 20
    endDate: { month: 11, day: 2 },     // November 2
    colors: {
      primary: '#ff6b35',
      secondary: '#8b4adb',
      accent: '#ffa500',
      dark: '#2d1b4e',
    },
    animations: {
      // Floating ghosts in background
      floatingGhosts: true,
      // Pumpkin emoji reactions
      emojiEffects: ['🎃', '👻', '🦇', '🕷️', '🕸️', '🧙'],
      // Spooky cursor trail
      cursorTrail: true,
    },
    decorations: {
      // Show decorations in specific locations
      sidebar: true,
      chatHeader: true,
      welcomeSplash: true,
      settings: false,
    }
  },

  melbourneCup: {
    id: 'melbourneCup',
    name: 'Melbourne Cup Carnival',
    enabled: true,
    // First Tuesday of November (typically Nov 5-7)
    startDate: { month: 11, day: 1 },
    endDate: { month: 11, day: 7 },
    colors: {
      primary: '#006a4e',
      secondary: '#ffd700',
      accent: '#c41e3a',
      dark: '#1a472a',
    },
    animations: {
      floatingHorses: true,
      emojiEffects: ['🏇', '🏆', '🎩', '👔', '🍾'],
      confetti: true,
    },
    decorations: {
      sidebar: true,
      chatHeader: true,
      welcomeSplash: true,
      settings: false,
    }
  },

  christmas: {
    id: 'christmas',
    name: 'Christmas',
    enabled: true,
    // December 15 to December 26
    startDate: { month: 12, day: 15 },
    endDate: { month: 12, day: 26 },
    colors: {
      primary: '#c41e3a',
      secondary: '#165b33',
      accent: '#ffd700',
      dark: '#0f3d23',
    },
    animations: {
      snowfall: true,
      emojiEffects: ['🎄', '🎅', '🎁', '⛄', '❄️', '🔔'],
      sparkles: true,
    },
    decorations: {
      sidebar: true,
      chatHeader: true,
      welcomeSplash: true,
      settings: false,
    }
  },

  blackFriday: {
    id: 'blackFriday',
    name: 'Black Friday Scratch & Win',
    enabled: true,
    // November 20 to December 1 (countdown week + Black Friday to Cyber Monday)
    startDate: { month: 11, day: 11 },
    endDate: { month: 12, day: 1 },
    colors: {
      primary: '#000000',      // Black
      secondary: '#C0C0C0',    // Silver scratch coating
      accent: '#FFD700',       // Gold for rare cards
      jackpot: '#FF00FF',      // Magenta for epic
      dark: '#0a0a0a',
    },
    animations: {
      scratchCards: true,      // Main mechanic
      scratchParticles: true,  // Flying scratch dust
      cardFloat: true,         // Cards hovering
      confettiReveal: true,    // On scratch complete
      emojiEffects: ['🎟️', '🎫', '💰', '🎉', '✨', '🏆'],
    },
    decorations: {
      sidebar: true,
      chatHeader: true,
      welcomeSplash: true,
      settings: false,
    }
  },

  newYear: {
    id: 'newYear',
    name: 'New Year',
    enabled: true,
    // December 31 to January 2
    startDate: { month: 12, day: 31 },
    endDate: { month: 1, day: 2 },
    colors: {
      primary: '#ffd700',
      secondary: '#4a90e2',
      accent: '#ff69b4',
      dark: '#1a1a2e',
    },
    animations: {
      fireworks: true,
      emojiEffects: ['🎉', '🎊', '🥂', '✨', '🎆', '🎇'],
      confetti: true,
    },
    decorations: {
      sidebar: true,
      chatHeader: true,
      welcomeSplash: true,
      settings: false,
    }
  },
};

// Helper function to check if a date is within a seasonal theme period
export const isDateInRange = (startDate, endDate) => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed
  const currentDay = now.getDate();

  // Handle year-wrap scenarios (e.g., Dec 31 to Jan 2)
  if (startDate.month > endDate.month) {
    // Year-wrapping case
    return (
      (currentMonth === startDate.month && currentDay >= startDate.day) ||
      (currentMonth === endDate.month && currentDay <= endDate.day) ||
      (currentMonth > startDate.month) ||
      (currentMonth < endDate.month)
    );
  }

  // Normal case
  if (currentMonth < startDate.month || currentMonth > endDate.month) {
    return false;
  }

  if (currentMonth === startDate.month && currentDay < startDate.day) {
    return false;
  }

  if (currentMonth === endDate.month && currentDay > endDate.day) {
    return false;
  }

  return true;
};

// Get active seasonal theme
export const getActiveSeasonalTheme = () => {
  for (const theme of Object.values(SEASONAL_THEMES)) {
    if (theme.enabled && isDateInRange(theme.startDate, theme.endDate)) {
      return theme;
    }
  }
  return null;
};

// Check if a specific theme is active
export const isThemeActive = (themeId) => {
  const theme = SEASONAL_THEMES[themeId];
  if (!theme || !theme.enabled) return false;
  return isDateInRange(theme.startDate, theme.endDate);
};
